<template>
  <div class="footer-container">
    <div class="footer-content container top-spacer bottom-spacer">
      <div class="footer-logos">
        <div class="footer-logo">
          <FooterLogo />
        </div>
      </div>
      <div class="footer-social">
        <a target="_blank" href="https://www.instagram.com/article/" rel="noreferrer" aria-label="Article's Instagram (opens in a new tab)">
          <i class="af af-instagram"></i>
        </a>
        <a target="_blank" href="https://www.pinterest.com/article/" rel="noreferrer" aria-label="Article's Pinterest (opens in a new tab)">
          <i class="af af-pinterest"></i>
        </a>
        <a target="_blank" href="https://www.facebook.com/article" rel="noreferrer" aria-label="Article's Facebook (opens in a new tab)">
          <i class="af af-facebook"></i>
        </a>
        <a target="_blank" href="https://twitter.com/articlemodern" rel="noreferrer" aria-label="Article's Twitter (opens in a new tab)">
          <i class="af af-twitter"></i>
        </a>
        <a target="_blank" href="https://www.youtube.com/c/articlemodern" rel="noreferrer" aria-label="Article's Youtube (opens in a new tab)">
          <i class="af af-youtube"></i>
        </a>
      </div>
      <div class="footer-links-menu">
        <div v-if="footerLinks.length !== 0">
          <div class="footer-links" v-for="block in footerLinks" :key="block.name">
            <a v-if="block.url" :href="block.url"
              ><h4>{{ block.name }}</h4>
            </a>
            <h4 v-else>{{ block.name }}</h4>
            <ul>
              <li v-for="item in block.items" :key="item.name">
                <a :href="item.url">{{ item.name }}</a>
              </li>
            </ul>
          </div>
        </div>

        <div v-else>
          <div class="footer-links">
            <h4>Help</h4>
            <ul>
              <li>
                <a :href="$router.resolve({ name: 'helpCenter' }).href">Help Center</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'delivery' }).href">Shipping</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'returns' }).href">Returns</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'contact' }).href">Contact Us</a>
              </li>
            </ul>
          </div>
          <div class="footer-links">
            <h4>Explore</h4>
            <ul>
              <li>
                <a href="https://www.article.com/blog/">Blog</a>
              </li>
              <li class="gift-card-link">
                <a :href="$router.resolve({ name: 'giftCardBuy' }).href">Gift Cards</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'financing' }).href">Financing</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'aboutus' }).href">About Us</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'customerReviews' }).href">Reviews</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'press' }).href">Press</a>
              </li>
              <li>
                <a href="https://article.pinpointhq.com/">Careers</a>
              </li>
            </ul>
          </div>
          <div class="footer-links account">
            <h4>Account</h4>
            <ul>
              <li>
                <a v-if="isVerifiedUser" :href="$router.resolve({ name: 'menu' }).href">My Account</a>
                <a v-else :href="$router.resolve({ name: 'login' }).href">Login/Register</a>
              </li>
            </ul>
          </div>

          <div class="footer-links">
            <h4>B2B</h4>
            <ul>
              <li>
                <a :href="$router.resolve({ name: 'tradeSignUp' }).href">Trade</a>
              </li>
              <li>
                <a :href="$router.resolve({ name: 'businessSignUp' }).href">Business</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="footer-country-feedback">
        <div class="footer-country">
          <img :src="currentCountry.flag" :alt="currentCountry.alt" class="lazyload" />
          <span class="active-country">{{ currentCountry.code }}</span> |
          <a @click="changeCountry({ country: otherCountry, shouldReload })">
            <span>{{ otherCountry }}</span>
          </a>
          <div class="footer-copyright">
            <a :href="$router.resolve({ name: 'privacyPolicy' }).href">Privacy Policy</a> -
            <a :href="$router.resolve({ name: 'termsOfUse' }).href">Terms of Use</a> -
            <template v-if="mounted && isCalifornia">
              <a :href="$router.resolve({ name: 'privacyPolicy', hash: '#california-privacy-notice' }).href">Do Not Sell My Personal Information </a>
              -
            </template>
            <a :href="$router.resolve({ name: 'accessibility' }).href">Accessibility</a> -
            <a :href="$router.resolve({ name: 'sitemap' }).href">Sitemap</a> -
            <a :href="$router.resolve({ name: 'shop' }).href">Shop</a>
            <br />
            Copyright &copy; {{ currentYear }} Article.com. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CountryUtils from '@/components/commons/CountryUtils.js';
import FooterLogo from '@/assets/logos/article_logo_white.svg';
import LocationService from '@/services/LocationService';
import useCommon from '@/compositions/useCommon';

export default {
  name: 'PageFooter',
  components: {
    FooterLogo,
  },

  setup() {
    const {
      zipLocation, //
      isVerifiedUser,
      currentCountry,
      changeCountry,
      footerNavigationLinks,
    } = useCommon();

    return {
      zipLocation, //
      isVerifiedUser,
      currentCountry,
      changeCountry,
      footerNavigationLinks,
    };
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      shouldReload: true,
      mounted: false,
    };
  },

  mounted() {
    this.mounted = true;
  },

  computed: {
    otherCountry() {
      return this.currentCountry.id === CountryUtils.CA.id ? CountryUtils.US.code : CountryUtils.CA.code;
    },

    isCalifornia() {
      return this.zipLocation && this.zipLocation.regionCode && this.zipLocation.regionCode === 'CA';
    },

    isUSA() {
      return LocationService.isUS();
    },

    footerLinks() {
      const loggedIn = this.isVerifiedUser;
      return this.footerNavigationLinks.map((block) => {
        return {
          ...block,
          items: block.items.filter((f) => {
            return !f.url || (loggedIn && f.url !== '/login') || (!loggedIn && f.url !== '/account');
          }),
        };
      });
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.footer-container {
  h4 {
    font-family: var(--font-bold);
  }
  background-color: var(--grey-dark);
  display: block;
  font-family: var(--font-bold);
  color: var(--white);

  .footer-content {
    .footer-logos {
      overflow: hidden;
      .footer-logo {
        display: inline-block;
        width: 230px;
        height: 30px;
      }
    }
    .footer-social {
      line-height: 50px;
      padding-top: 10px;
      a {
        margin-right: 20px;
        float: left;
        color: var(--white);

        .af {
          font-size: 1.8em;
        }
      }

      a:visited {
        color: white;
      }

      a:focus,
      a:active,
      a:hover {
        color: var(--grey-light);
      }
    }

    .footer-links-menu {
      text-transform: uppercase;
      overflow: hidden;
      clear: both;
      .footer-links {
        width: 46%;
        float: left;
        padding-top: 20px;
        @media (max-width: 320px) {
          margin-right: var(--space-2);
        }
        h4 {
          color: white;
          white-space: nowrap;

          @media (max-width: calc($mobile-breakpoint - 1px)) {
            white-space: pre-wrap;
          }
        }
        ul {
          list-style-type: none;
          padding: 0;
          margin: 0;
          margin-top: 10px;
          li {
            padding: 0;
            margin: 0;
            line-height: 1em;
            a {
              display: block;
              font-size: var(--font-size-12);
              line-height: var(--line-height-12);
              letter-spacing: 1px;
              color: white;
            }

            a:visited {
              color: white;
            }

            a:focus,
            a:active,
            a:hover {
              color: var(--grey-light);
            }

            a.email {
              text-transform: lowercase;
            }

            @media (max-width: calc($mobile-breakpoint - 1px)) {
              a {
                padding: 4px 0px;
              }
            }
          }
        }
        margin-left: 0;
      }
      .links-right {
        margin-left: 8%;
      }
    }

    .footer-country-feedback {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-top: 20px;

      .footer-country {
        img {
          width: 20px;
          height: 20px;
          display: inline-block;
          margin-right: 10px;
          margin-bottom: -4px;
        }

        a {
          font-family: var(--font-regular);
          color: var(--grey-ultra-dark);

          /* purgecss ignore */
          & :focus,
          & :active,
          & :hover {
            color: var(--white);
          }
        }
        .footer-copyright {
          font-size: var(--font-size-14);
          overflow: hidden;
          margin-top: 10px;
          a {
            color: white;
          }

          a:visited {
            color: white;
          }

          a:focus,
          a:active,
          a:hover {
            color: var(--grey-light);
          }
        }
      }

      @media (min-width: $mobile-breakpoint) {
        margin-top: 20px;
      }
    }
  }
}

@media (min-width: 700px) {
  .footer-container {
    .footer-content {
      .footer-links-menu {
        .footer-links {
          width: 25%;
          max-width: 200px;
          margin-left: 0;
        }
      }
      .footer-copyright {
        font-size: 0.9em;
      }
    }
  }
}

.page-footer-container {
  background-color: var(--grey-dark);
  padding: 30px 0px 20px;
  font-family: var(--font-bold);
  color: var(--white);

  .footer-content {
    display: flex;
    flex-direction: column;

    .footer-logo {
      height: 30px;
      margin-bottom: 10px;

      img {
        height: 100%;
      }
    }

    .footer-social {
      line-height: 50px;

      a {
        margin-right: 20px;
        color: var(--white);

        .af {
          font-size: 1.8em;
        }
      }

      a:visited {
        color: white;
      }

      a:focus,
      a:active,
      a:hover {
        color: var(--grey-light);
      }
    }

    .footer-links-menu {
      text-transform: uppercase;

      .footer-links {
        float: left;
        width: 46%;
        margin-top: 20px;

        h4 {
          font-family: var(--font-bold);
          color: white;
        }

        ul {
          list-style-type: none;
          padding: 0;
          margin: 0;
          margin-top: 10px;

          li {
            padding: 0;
            margin: 0;
            line-height: 1em;

            a {
              font-size: var(--font-size-14);
              letter-spacing: 1px;
              color: white;
            }

            a:visited {
              color: white;
            }

            a:focus,
            a:active,
            a:hover {
              color: var(--grey-light);
            }

            a.email {
              text-transform: lowercase;
            }
          }
        }

        @media (min-width: 701px) {
          width: 25%;
          max-width: 200px;
        }
      }
    }

    .footer-country-feedback {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-top: 20px;

      .footer-country {
        img {
          width: 20px;
          height: 20px;
          display: inline-block;
          margin-right: 10px;
          margin-bottom: -4px;
        }

        a {
          font-family: var(--font-regular);
          color: var(--grey-ultra-dark);

          /* purgecss ignore */
          &:focus,
          &:active,
          &:hover {
            color: var(--white);
          }
        }
        .footer-copyright {
          font-size: var(--font-size-14);
          overflow: hidden;
          margin-top: 10px;
          a {
            color: white;
          }

          a:visited {
            color: white;
          }

          a:focus,
          a:active,
          a:hover {
            color: var(--grey-light);
          }
        }
      }

      @media (min-width: $mobile-breakpoint) {
        margin-top: 20px;
      }
    }
  }
}
</style>
